"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import { createReadingTest, type ReadingQuestion, type ReadingUnit } from "@/lib/atsa/verbal";
import { formatTime } from "@/lib/atsa/utils";

type Phase = "options" | "running" | "result";
type FlatQuestion = { passageId: string; passage: string; passageNumber: number; questionNumber: number; question: ReadingQuestion };

function flatten(units: ReadingUnit[]): FlatQuestion[] {
  return units.flatMap((unit, passageIndex) => unit.questions.map((question, questionIndex) => ({
    passageId: unit.id,
    passage: unit.passage,
    passageNumber: passageIndex + 1,
    questionNumber: questionIndex + 1,
    question,
  })));
}

export default function ReadingComprehensionPage() {
  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [durationSeconds, setDurationSeconds] = useState(15 * 60);
  const [passageCount, setPassageCount] = useState(3);
  const [questions, setQuestions] = useState<FlatQuestion[]>([]);
  const [answers, setAnswers] = useState<Array<number | null>>([]);
  const [current, setCurrent] = useState(0);
  const [remaining, setRemaining] = useState(15 * 60);

  const effectivePassages = mode === "simulation" ? ATSA_PROFILE_2026.reading.passages : passageCount;
  const effectiveDuration = mode === "simulation" ? ATSA_PROFILE_2026.reading.seconds : durationSeconds;

  const begin = useCallback(() => {
    const test = flatten(createReadingTest(effectivePassages, Date.now()));
    setQuestions(test);
    setAnswers(Array.from({ length: test.length }, () => null));
    setCurrent(0);
    setRemaining(effectiveDuration);
    setPhase("running");
  }, [effectiveDuration, effectivePassages]);

  useEffect(() => {
    if (phase !== "running") return;
    const timer = window.setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          setPhase("result");
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  function choose(index: number) {
    setAnswers((currentAnswers) => currentAnswers.map((answer, answerIndex) => answerIndex === current ? index : answer));
  }

  function next() {
    if (current + 1 < questions.length) setCurrent((value) => value + 1);
    else setPhase("result");
  }

  const item = questions[current];
  const correct = useMemo(() => questions.reduce((sum, question, index) => sum + (answers[index] === question.question.answer ? 1 : 0), 0), [answers, questions]);
  const activeSimulation = mode === "simulation" && phase === "running";

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Reading Comprehension"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase === "running" ? `Question ${current + 1} of ${questions.length} · ${formatTime(remaining)}` : undefined}
        onRestart={mode === "training" && phase !== "options" ? begin : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 6</p>
              <h2>Reading Comprehension</h2>
              <p>Read six original passages and answer three questions per passage. Questions target main idea, explicit support, and inference.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") {
                setPassageCount(6);
                setDurationSeconds(15 * 60);
              }
            }} />
            {mode === "training" && (
              <div className="options-grid">
                <div className="profile-block">
                  <span className="profile-label">Difficulty profile</span>
                  <div className="profile-row">
                    {([['Light', 'Warm-up', 3, 1200, false], ['Standard', 'Test-like', 6, 900, true], ['Timed', 'Harder', 6, 600, false]] as const).map(([label, tag, pc, secs, std]) => (
                      <button type="button" key={label} className={`profile-chip ${passageCount === pc && durationSeconds === secs ? "active" : ""}`} onClick={() => { setPassageCount(pc); setDurationSeconds(secs); }}>
                        <span className="tag">{tag}</span>{label}{std ? <span className="std-flag">★ Standard</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="control-group"><label htmlFor="reading-passages">Passages</label><select id="reading-passages" value={passageCount} onChange={(event) => setPassageCount(Number(event.target.value))}><option value={2}>2</option><option value={3}>3</option><option value={6}>6</option></select></div>
                <div className="control-group"><label htmlFor="reading-time">Time limit</label><select id="reading-time" value={durationSeconds} onChange={(event) => setDurationSeconds(Number(event.target.value))}><option value={10 * 60}>10 minutes</option><option value={15 * 60}>15 minutes</option><option value={20 * 60}>20 minutes</option></select></div>
              </div>
            )}
            <ol className="instructions">
              <li>The primary profile contains six passages and 18 questions.</li>
              <li>Public reports conflict between 15 and 20 minutes. Fidelity simulation uses 15 minutes.</li>
              <li>Fidelity mode commits each answer and does not permit backtracking.</li>
              <li>Every passage and question in this project is original.</li>
            </ol>
            <button className="primary-button" onClick={begin}>Start {mode === "simulation" ? "Simulation" : "Training"}</button>
          </section>
        )}

        {phase === "running" && item && (
          <section className={activeSimulation ? "fidelity-verbal" : "question-card verbal-card"}>
            <div className="verbal-columns">
              <article className="reading-passage">
                <p className="verbal-label">Passage {item.passageNumber} of {effectivePassages}</p>
                <p>{item.passage}</p>
              </article>
              <div className="reading-question">
                <p className="verbal-label">Question {item.questionNumber} of 3</p>
                <h3>{item.question.prompt}</h3>
                <div className="answer-list light-answer-list">
                  {item.question.options.map((option, index) => (
                    <label className={`answer-option ${answers[current] === index ? "selected" : ""}`} key={option}>
                      <input type="radio" name="reading-answer" checked={answers[current] === index} onChange={() => choose(index)} />
                      <strong>{String.fromCharCode(65 + index)}.</strong>
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
                <div className="verbal-actions">
                  {mode === "training" && current > 0 && <button className="secondary-button" onClick={() => setCurrent((value) => value - 1)}>Previous</button>}
                  <button className="primary-button" disabled={answers[current] === null} onClick={next}>{current + 1 === questions.length ? "Finish" : "Next"}</button>
                </div>
              </div>
            </div>
          </section>
        )}

        {phase === "result" && (
          <>
            <Results
              section="Reading Comprehension"
              record={{ sectionSlug: "reading-comprehension", score: questions.length ? Math.round((correct / questions.length) * 100) : 0, scoreLabel: "Accuracy" }}
              title="Reading Comprehension Complete"
              metrics={[
                { label: "Correct", value: correct },
                { label: "Incorrect", value: answers.filter((answer) => answer !== null).length - correct },
                { label: "Unanswered", value: answers.filter((answer) => answer === null).length },
                { label: "Accuracy", value: `${questions.length ? Math.round((correct / questions.length) * 100) : 0}%` },
              ]}
              onAgain={begin}
              onOptions={() => setPhase("options")}
            />
            {mode === "training" && questions.length > 0 && (
              <section className="panel review-panel"><h3>Answer review</h3>{questions.map((question, index) => <div className={`review-item ${answers[index] === question.question.answer ? "correct" : "wrong"}`} key={`${question.passageId}-${index}`}><strong>{index + 1}. {question.question.prompt}</strong><p>{question.question.explanation}</p></div>)}</section>
            )}
          </>
        )}
      </TestChrome>
    </AppShell>
  );
}
